// #region: DialogWindow Class Definition ***************************************/
/**
 * @class DialogWindow
 * @description Custom web component that renders a resizable and draggable (modal) dialog.
 * Supports both declarative HTML usage and programmatic creation with cross-window support.
 * 
 * @example
 * // Declarative HTML usage
 * <dialog-window id="my-dialog" icon="lf-icon-pallet" width="800" height="600">
 * </dialog-window>
 * 
 * @example
 * // Programmatic creation
 * const dialog = DialogWindow.create({
 *     id: 'dynamic-dialog',
 *     title: 'My Dialog',
 *     owner: 'current',
 *     width: 1000,
 *     height: 700
 * });
 * 
 * // Open with content
 * dialog.open('/some/url', {
 *     title: 'Updated Title',
 *     data: { key: 'value' }
 * });
 * 
 * // Reuse the same dialog
 * dialog.open('/other/url');
 * 
 * // Clean up when done
 * dialog.destroy();
 * 
 * @fires DialogWindow#dialogopen - Fired when the dialog opens
 * @fires DialogWindow#dialogclose - Fired when the dialog closes
 * @fires DialogWindow#dialogresize - Fired when the dialog is resized
 * 
 * @extends WebComponent
 */
class DialogWindow extends WebComponent {

    // #region: Static Data **********************************************************************/
    /**
     * Minimum height allowed for the dialog window.
     * @type {number}
     * @static
     *
     * @memberof DialogWindow
     */
    static MIN_DIALOG_HEIGHT = 150;

    /**
     * Minimum width allowed for the dialog window.
     * @type {number}
     * @static
     *
     * @memberof DialogWindow
     */
    static MIN_DIALOG_WIDTH = 300;

    /**
     * Maintains the stack of open (non-headless) dialogs for z-index ordering.
     * Newer dialogs appear later in the array and stack above older ones.
     * @type {DialogWindow[]}
     * @private
     * @static
     */
    static #openDialogs = [];

    /**
     * Small screen breakpoint in pixels (32rem = 512px).
     * Below this width, the dialog uses mobile behavior.
     * @type {number}
     * @static
     *
     * @memberof DialogWindow
     */
    static SMALL_SCREEN_BREAKPOINT = 512;

    /**
     * Base z-index used for stacking dialog layers (wrapper/backdrop).
     * @type {number}
     * @private
     * @static
     */
    static #Z_BASE = 1000;

    /**
     * Step interval between dialog stacks.
     * @type {number}
     * @private
     * @static
     */
    static #Z_STEP = 10;
    // #endregion ********************************************************************************/

    // #region: Private Fields *******************************************************************/
    /**
     * Abort controller for cleaning up event listeners.
     * @type {AbortController|null}
     * @private
     */
    #abortController;

    /**
     * Flag indicating if the dialog automatically sizes to fit its content.
     * @type {boolean}
     * @private
     */
    #autoSize = false;

    /**
     * Flag indicating if the dialog is clamped to viewport boundaries.
     * @type {boolean}
     * @private
     */
    #clampToViewport = false;

    /**
     * Horizontal offset from the mouse cursor to the dialog container during drag operations.
     * @type {number|undefined}
     * @private
     */
    #clickOffsetX;

    /**
     * Vertical offset from the mouse cursor to the dialog container during drag operations.
     * @type {number|undefined}
     * @private
     */
    #clickOffsetY;

    /**
     * Callback function executed when the dialog closes.
     * @type {Function|null}
     * @private
     */
    #closeCallback = null;

    /**
     * Reference to the close button element.
     * @type {HTMLButtonElement|null}
     * @private
     */
    #closeButton;

    /**
     * Reference to the currently active resize handle during resize operations.
     * @type {HTMLElement|null}
     * @private
     */
    #currentHandle = null;

    /**
     * Custom data associated with the dialog instance.
     * @type {object|null}
     * @private
     */
    #data;

    /**
     * Reference to the dialog wrapper element (headless mode: native dialog element, regular mode: div element).
     * @type {HTMLDialogElement|HTMLDivElement|null}
     * @private
     */
    #dialog;

    /**
     * Reference to the dialog backdrop element (null for headless mode).
     * @type {HTMLDivElement|null}
     * @private
     */
    #dialogBackdrop;

    /**
     * Reference to the main dialog container element.
     * @type {HTMLDivElement|null}
     * @private
     */
    #dialogContainer;

    /**
     * Reference to the dialog header element (null for headless mode).
     * @type {HTMLDivElement|null}
     * @private
     */
    #dialogHeader;

    /**
     * Reference to the dialog title element.
     * @type {HTMLHeadingElement|null}
     * @private
     */
    #dialogTitleElement;

    /**
     * Unique key for this dialog's history state used for back button behavior.
     * @type {string|null}
     * @private
     */
    #historyStateKey = null;

    /**
     * Reference to the iframe element containing the dialog content.
     * @type {HTMLIFrameElement|null}
     * @private
     */
    #iframe;

    /**
     * Initial height of the dialog before resize operation.
     * @type {number|undefined}
     * @private
     */
    #initialHeight;

    /**
     * Initial left position of the dialog before resize operation.
     * @type {number|undefined}
     * @private
     */
    #initialLeft;

    /**
     * Initial top position of the dialog before resize operation.
     * @type {number|undefined}
     * @private
     */
    #initialTop;

    /**
     * Initial width of the dialog before resize operation.
     * @type {number|undefined}
     * @private
     */
    #initialWidth;

    /**
     * Flag indicating if the dialog is currently being dragged.
     * @type {boolean}
     * @private
     */
    #isDragging = false;

    /**
     * Flag indicating if the dialog is currently being resized.
     * @type {boolean}
     * @private
     */
    #isResizing = false;

    /**
     * Cached locale resources for this component.
     * @type {Object}
     * @private
     */
    #locales;

    /**
     * Reference to the window where the dialog was originally opened from.
     * @type {Window|null}
     * @private
     */
    #originWindow;

    /**
     * Bound popstate event handler for back button dialog closure.
     * @type {Function|null}
     * @private
     */
    #popstateHandler = null;

    /**
     * Collection of resize handle elements (empty for headless mode).
     * @type {NodeListOf<Element>|null}
     * @private
     */
    #resizeHandles;

    /**
     * Resize observer for responsive adjustments.
     * @type {ResizeObserver|null}
     * @private
     */
    #resizeObserver;

    /**
     * Starting X coordinate for resize or drag operations.
     * @type {number|undefined}
     * @private
     */
    #startX;

    /**
     * Starting Y coordinate for resize or drag operations.
     * @type {number|undefined}
     * @private
     */
    #startY;

    /**
     * Reference to the window where the dialog is opened in.
     * @type {Window|null}
     * @private
     */
    #targetWindow;
    // #endregion ********************************************************************************/

    // #region: Constructor **********************************************************************/
    /**
     * The constructor does work that needs to be executed exactly once.
     */
    constructor() {
        super();

        // Register locale namespaces used by this component and cache resources.
        this.#locales = this.registerLocaleNamespace();

        // Create abort controller for event cleanup.
        this.#abortController = new AbortController();
    }
    // #endregion ********************************************************************************/

    // #region: Build ****************************************************************************/
    /**
     * Registered tag name for this web component.
     * This is used by the browser to identify and register the custom element.
     * @type {string}
     * @static
     *
     * @memberof DialogWindow
     */
    static tagName = 'dialog-window';

    /**
     * Identifies class.
     * @returns {string} The string representation of this class.
     */
    toString() {
        return '[DialogWindow]';
    }

    /**
     * Identifies class for static context.
     * @returns {string} The string representation of this class.
     */
    static toString() {
        return '[DialogWindow]';
    }

    /**
     * Identifies the version.
     * @returns {string} The version number of this class.
     */
    static version() {
        return '1.0.0';
    }
    // #endregion ********************************************************************************/

    // #region: Static Properties ****************************************************************/
    /**
     * Gets the dialog instance.
     * @returns {DialogWindow} The current dialog instance.
     *
     * @memberof DialogWindow
     */
    static get instance() {
        return window.dialogWindow || window.frameElement?.dialogWindow;
    }

    /**
     * Defines the observed attributes for the custom element.
     * @returns {string[]} Array of attribute names to observe.
     * @static
     */
    static get observedAttributes() {
        return [...(super.observedAttributes || []), 'auto-size', 'clamp-to-viewport', 'data-dialog-title', 'height', 'icon', 'modal', 'src', 'width'];
    }
    // #endregion ********************************************************************************/

    // #region: Static Methods *******************************************************************/
    /**
     * Recomputes z-index layering so each dialog's backdrop overlays its opener,
     * and its window overlays its own backdrop.
     * @private
     * @static
     */
    static #recomputeZOrder() {
        let index = 0;

        for (const dlg of DialogWindow.#openDialogs) {
            const base = DialogWindow.#Z_BASE + (index * DialogWindow.#Z_STEP);

            // Backdrop sits above anything below this dialog, but below the dialog itself.
            if (dlg.#dialogBackdrop) {
                dlg.#dialogBackdrop.style.zIndex = String(base + 1);
            }

            // Dialog wrapper sits above its backdrop.
            if (dlg.#dialog) {
                dlg.#dialog.style.zIndex = String(base + 2);
            }

            index += 1;
        }
    }
    // #endregion ********************************************************************************/
 
    // #region: Properties ***********************************************************************/
    /**
     * Gets the auto-size setting.
     * @returns {boolean} Whether the dialog automatically sizes to fit its content.
     *
     * @memberof DialogWindow
     */
    get autoSize() {
        return this.#autoSize;
    }

    /**
     * Sets the auto-size setting.
     * @param {boolean} value Whether to automatically size the dialog to fit its content.
     *
     * @memberof DialogWindow
     */
    set autoSize(value) {
        const shouldAutoSize = Boolean(value);

        if (shouldAutoSize !== this.#autoSize) {
            this.#autoSize = shouldAutoSize;
            this.setAttribute('auto-size', shouldAutoSize.toString());
        }
    }

    /**
     * Gets the clamp-to-viewport setting.
     * @returns {boolean} Whether the dialog is clamped to viewport boundaries.
     *
     * @memberof DialogWindow
     */
    get clampToViewport() {
        return this.#clampToViewport;
    }

    /**
     * Sets the clamp-to-viewport setting.
     * @param {boolean} value Whether to clamp the dialog to viewport boundaries.
     *
     * @memberof DialogWindow
     */
    set clampToViewport(value) {
        const shouldClamp = Boolean(value);

        if (shouldClamp !== this.#clampToViewport) {
            this.#clampToViewport = shouldClamp;
            this.setAttribute('clamp-to-viewport', shouldClamp.toString());
        }
    }

    /**
     * Gets any data associated with the dialog.
     * @returns {object|null} The data, or null.
     *
     * @memberof DialogWindow
     */
    get data() {
        return this.#data;
    }

    /**
     * Gets whether the dialog is in headless mode.
     * @returns {boolean} Whether the dialog is headless.
     *
     * @memberof DialogWindow
     */
    get headless() {
        return Boolean(this.isHeadless);
    }

    /**
     * Gets the icon class name on the button.
     * @returns {string|null} The icon class name or null if not set.
     *
     * @memberof DialogWindow
     */
    get icon() {
        return this.getAttribute('icon');
    }

    /**
     * Sets the icon class name on the button.
     * @param {string} value The icon class name to set.
     *
     * @memberof DialogWindow
     */
    set icon(value) {
        this.setAttribute('icon', value);
    }

    /**
     * Checks if dialog is open.
     * @returns {boolean} Whether the dialog is open.
     *
     * @memberof DialogWindow
     */
    get isOpen() {
        if (this.headless) {
            return this.#dialog?.open || false;
        }

        return this.#dialog?.style.display === 'flex' || false;
    }

    /**
     * Gets the origin window reference (the window where the dialog was originally opened from).
     * This is used for postMessage communication from iframe content back to the originating window and to access global data available in that window.
     * @returns {Window|null} The origin window reference or null if not set.
     *
     * @memberof DialogWindow
     */
    get originWindow() {
        return this.#originWindow;
    }

    /**
     * Sets the origin window reference (the window where the dialog was originally opened from).
     * This is used internally by the static create method.
     * @param {Window} window The origin window reference.
     *
     * @memberof DialogWindow
     */
    set originWindow(window) {
        this.#originWindow = window;
    }

    /**
     * Gets the target window the dialog is opened in.
     * @returns {string} The window reference.
     *
     * @memberof DialogWindow
     */
    get targetWindow() {
        return this.#targetWindow;
    }

    /**
     * Sets the target window the dialog is opened in.
     * @param {string} value The window reference.
     *
     * @memberof DialogWindow
     */
    set targetWindow(value) {
        this.#targetWindow = value;
    }

    /**
     * Gets the dialog title.
     * @returns {string} The current dialog title.
     *
     * @memberof DialogWindow
     */
    get title() {
        return this.getAttribute('data-dialog-title') || '';
    }

    /**
     * Sets the dialog title.
     * @param {string} value The new dialog title.
     *
     * @memberof DialogWindow
     */
    set title(value) {
        if (value) {
            this.setAttribute('data-dialog-title', value);
        }
        else {
            this.removeAttribute('data-dialog-title');
        }
    }
    // #endregion ********************************************************************************/

    // #region: Lifecycle events *****************************************************************/
    /**
     * Called when attributes change.
     * @param {string} name The name of the attribute.
     * @param {string} oldValue The old value of the attribute.
     * @param {string} newValue The new value of the attribute.
     *
     * @memberof DialogWindow
     */
    attributeChangedCallback(name, oldValue, newValue) {
        // Component not yet initialized.
        if (!this.#dialog) {
            return;
        }

        switch (name) {

            case 'auto-size':
                this.#autoSize = newValue === 'true';

                break;

            case 'clamp-to-viewport':
                this.#clampToViewport = newValue === 'true';

                break;

            case 'data-dialog-title':
                if (this.#dialogTitleElement) {
                    this.#dialogTitleElement.textContent = core.htmlDecode(newValue);
                }

                break;

            case 'height':
                if (this.#dialogContainer) {
                    this.#dialogContainer.style.height = `${newValue}px`;
                }

                break;

            case 'modal':
                // Update backdrop visibility based on modal state.
                if (this.#dialogBackdrop) {
                    if (newValue === 'true') {
                        this.#dialogBackdrop.style.display = 'block';
                    }
                    else {
                        this.#dialogBackdrop.style.display = 'none';
                    }
                }

                break;

            case 'src':
                // Use location.replace() to navigate without adding to the joint session history.
                // This keeps the browser back button clean for dialog history management.
                this.#iframe.contentWindow.location.replace(newValue);

                break;

            case 'width':
                if (this.#dialogContainer) {
                    this.#dialogContainer.style.width = `${newValue}px`;
                }

                break;

            default:
                break;

        }
    }

    /**
     * Called when the component is removed from the DOM.
     *
     * @memberof DialogWindow
     */
    disconnectedCallback() {
        super.disconnectedCallback();

        // Abort all event listeners at once.
        this.#abortController.abort();

        // Disconnect resize observer.
        if (this.#resizeObserver) {
            this.#resizeObserver.disconnect();
        }

        // Clean up history management.
        this.#cleanupHistoryManagement();

        // Ensure removal from stacking order when element is detached.
        const idx = DialogWindow.#openDialogs.indexOf(this);

        if (idx !== -1) {
            DialogWindow.#openDialogs.splice(idx, 1);
            DialogWindow.#recomputeZOrder();
        }

        // Clear any remaining references.
        this.#data = null;

        // Clear pending data if it exists.
        if (this.pendingData) {
            delete this.pendingData;
        }
    }
    // #endregion ********************************************************************************/

    // #region: Events ***************************************************************************/
    /**
     * Handles the dragging movement of the dialog.
     * @param {MouseEvent} event The mouse move event.
     * @private
     *
     * @memberof DialogWindow
     */
    #doDrag = (event) => {
        let newLeft = event.clientX - this.#clickOffsetX,
            newTop = event.clientY - this.#clickOffsetY;

        // Apply viewport constraints only if clamping is enabled.
        if (this.#clampToViewport) {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const dialogRect = this.#dialogContainer.getBoundingClientRect();

            newLeft = Math.max(0, Math.min(newLeft, viewportWidth - dialogRect.width));
            newTop = Math.max(0, Math.min(newTop, viewportHeight - dialogRect.height));
        }

        this.#dialogContainer.style.left = `${newLeft}px`;
        this.#dialogContainer.style.top = `${newTop}px`;
    };

    /**
     * Handles the resizing of the dialog.
     * @param {MouseEvent} event The mouse move event.
     * @private
     *
     * @memberof DialogWindow
     */
    #doResize = (event) => {
        const dy = event.clientY - this.#startY;
        const dx = event.clientX - this.#startX;

        let newDialogLeft = this.#initialLeft,
            newDialogTop = this.#initialTop,
            newHeight = this.#initialHeight,
            newWidth = this.#initialWidth;

        const handleClasses = this.#currentHandle.classList;

        // Resize width based on handle.
        if (handleClasses.contains('right') || handleClasses.contains('top-right') || handleClasses.contains('bottom-right')) {
            newWidth = Math.max(DialogWindow.MIN_DIALOG_WIDTH, this.#initialWidth + dx);
        }

        if (handleClasses.contains('left') || handleClasses.contains('top-left') || handleClasses.contains('bottom-left')) {
            const calculatedWidth = this.#initialWidth - dx;
            if (calculatedWidth >= DialogWindow.MIN_DIALOG_WIDTH) {
                newWidth = calculatedWidth;
                newDialogLeft = this.#initialLeft + dx;
            }
            else {
                newWidth = DialogWindow.MIN_DIALOG_WIDTH;
                newDialogLeft = this.#initialLeft + (this.#initialWidth - DialogWindow.MIN_DIALOG_WIDTH);
            }
        }

        // Resize height based on handle.
        if (handleClasses.contains('bottom') || handleClasses.contains('bottom-left') || handleClasses.contains('bottom-right')) {
            newHeight = Math.max(DialogWindow.MIN_DIALOG_HEIGHT, this.#initialHeight + dy);
        }

        if (handleClasses.contains('top') || handleClasses.contains('top-left') || handleClasses.contains('top-right')) {
            const calculatedHeight = this.#initialHeight - dy;
            if (calculatedHeight >= DialogWindow.MIN_DIALOG_HEIGHT) {
                newHeight = calculatedHeight;
                newDialogTop = this.#initialTop + dy;
            }
            else {
                newHeight = DialogWindow.MIN_DIALOG_HEIGHT;
                newDialogTop = this.#initialTop + (this.#initialHeight - DialogWindow.MIN_DIALOG_HEIGHT);
            }
        }

        // Apply new dimensions.
        this.#dialogContainer.style.width = `${newWidth}px`;
        this.#dialogContainer.style.height = `${newHeight}px`;

        // Apply new position if needed.
        if (handleClasses.contains('left') || handleClasses.contains('top-left') || handleClasses.contains('bottom-left') ||
            handleClasses.contains('top') || handleClasses.contains('top-right')) {
            this.#dialogContainer.style.left = `${newDialogLeft}px`;
            this.#dialogContainer.style.top = `${newDialogTop}px`;
        }

        // Dispatch resize event.
        this.dispatchEvent(new CustomEvent('dialogresize', {
            bubbles: true,
            composed: true,
            detail: { height: newHeight, width: newWidth }
        }));
    };

    /**
     * Handles the click event on the close button.
     * @private
     *
     * @memberof DialogWindow
     */
    #handleCloseButtonClick = () => {
        this.close();
    };

    /**
     * Handles the close of the dialog.
     * @private
     *
     * @memberof DialogWindow
     */
    #handleDialogClose = () => {
        // Restore body scrolling.
        this.#unlockScroll();

        // Clean up any active resize/drag state.
        this.#stopDrag();
        this.#stopResize();

        // Clean up history management.
        this.#cleanupHistoryManagement();

        // Call the close callback if provided.
        if (this.#closeCallback && typeof this.#closeCallback === 'function') {
            try {
                this.#closeCallback(new CustomEvent('dialogclose', {
                    bubbles: true,
                    composed: true
                }));
            }
            catch (error) {
                console.error('DialogWindow: Error in close callback:', error);
            }
        }

        // Dispatch a custom event to notify that the dialog has closed.
        this.dispatchEvent(new CustomEvent('dialogclose', {
            bubbles: true,
            composed: true
        }));
    };

    /**
     * Initiates the dragging process for the dialog.
     * @param {MouseEvent} event The mousedown event.
     * @returns {void}
     * @private
     *
     * @memberof DialogWindow
     */
    #handleHeaderDown = (event) => {
        // Headless dialogs don't support dragging.
        if (this.headless) {
            return;
        }
        
        if (this.#isResizing || (event.target !== this.#dialogHeader && event.target !== this.#dialogTitleElement)) {
            return;
        }

        event.preventDefault();

        this.#isDragging = true;
        
        if (this.#dialogHeader) {
            this.#dialogHeader.classList.add('dragging');
        }

        const dialogRect = this.#dialogContainer.getBoundingClientRect();

        this.#clickOffsetX = event.clientX - dialogRect.left;
        this.#clickOffsetY = event.clientY - dialogRect.top;

        // Prepare for positioning.
        this.#prepareForPositioning();

        // Pin to current visual position.
        this.#dialogContainer.style.top = `${dialogRect.top}px`;
        this.#dialogContainer.style.left = `${dialogRect.left}px`;

        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'grabbing';
    };

    /**
     * Handles keyboard navigation and shortcuts.
     * @param {KeyboardEvent} event The keyboard event.
     * @private
     *
     * @memberof DialogWindow
     */
    #handleKeyDown = (event) => {
        if (event.key === 'Escape' && this.isOpen) {
            event.preventDefault();

            this.close();
        }
    };

    /**
     * Handles document-level keyboard events for headless dialogs.
     * @param {KeyboardEvent} event The keyboard event.
     * @private
     *
     * @memberof DialogWindow
     */
    #handleDocumentKeyDown = (event) => {
        // ESC key support for headless dialogs.
        if (event.key === 'Escape' && this.headless && this.isOpen) {
            event.preventDefault();
            this.close();
        }
    };

    /**
     * Initiates the resizing process for the dialog.
     * @param {MouseEvent} event The mousedown event.
     * @private
     *
     * @memberof DialogWindow
     */
    #handleResizeHandleDown = (event) => {
        event.preventDefault();

        this.#isResizing = true;
        this.#currentHandle = event.target;

        this.#startX = event.clientX;
        this.#startY = event.clientY;

        this.#initialWidth = this.#dialogContainer.offsetWidth;
        this.#initialHeight = this.#dialogContainer.offsetHeight;

        const dialogRect = this.#dialogContainer.getBoundingClientRect();
        this.#initialTop = dialogRect.top;
        this.#initialLeft = dialogRect.left;

        // Prepare for positioning.
        this.#prepareForPositioning();

        // Pin to current visual position.
        this.#dialogContainer.style.top = `${dialogRect.top}px`;
        this.#dialogContainer.style.left = `${dialogRect.left}px`;

        document.body.style.userSelect = 'none';
        document.body.style.cursor = getComputedStyle(this.#currentHandle).cursor;
        this.#dialogContainer.classList.add('resizing');
    };

    /**
     * Delegates to either drag or resize based on the current mode.
     * @param {MouseEvent} event The mouse move event.
     * @private
     *
     * @memberof DialogWindow
     */
    #handleResizeOrDrag = (event) => {
        if (this.#isResizing) {
            this.#doResize(event);
        }
        else if (this.#isDragging) {
            this.#doDrag(event);
        }
    };

    /**
     * Stops either resize or drag operations on mouseup.
     * @param {MouseEvent} event The mouse move event.
     * @private
     *
     * @memberof DialogWindow
     */
    #handleStopResizeOrDrag = () => {
        if (this.#isDragging) {
            this.#stopDrag();
        }

        if (this.#isResizing) {
            this.#stopResize();
        }
    };

    /**
     * Handles touch end for mobile dragging.
     * @param {TouchEvent} event The touch end event.
     * @private
     *
     * @memberof DialogWindow
     */
    #handleTouchEnd = (event) => {
        if (this.#isDragging) {
            this.#stopDrag();
        }
    };

    /**
     * Handles touch move for mobile dragging.
     * @param {TouchEvent} event The touch move event.
     * @private
     *
     * @memberof DialogWindow
     */
    #handleTouchMove = (event) => {
        if (!this.#isDragging) {
            return;
        }

        event.preventDefault();
        const touch = event.touches[0];

        let newLeft = touch.clientX - this.#clickOffsetX,
            newTop = touch.clientY - this.#clickOffsetY;

        // Apply viewport constraints only if clamping is enabled.
        if (this.#clampToViewport) {
            const viewportWidth = window.innerWidth;
            const viewportHeight = window.innerHeight;
            const dialogRect = this.#dialogContainer.getBoundingClientRect();

            newLeft = Math.max(0, Math.min(newLeft, viewportWidth - dialogRect.width));
            newTop = Math.max(0, Math.min(newTop, viewportHeight - dialogRect.height));
        }

        this.#dialogContainer.style.left = `${newLeft}px`;
        this.#dialogContainer.style.top = `${newTop}px`;
    };

    /**
     * Handles focus trapping for modal dialogs.
     * @param {KeyboardEvent} event The keyboard event.
     * @private
     *
     * @memberof DialogWindow
     */
    #trapFocus = (event) => {
        if (!this.isOpen || this.headless || event.key !== 'Tab') {
            return;
        }

        const focusables = this.#dialogContainer.querySelectorAll('a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])');

        if (focusables.length === 0) {
            return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = this.ownerDocument.activeElement;

        if (event.shiftKey && active === first) {
            event.preventDefault();

            last.focus();

            return;
        }

        if (!event.shiftKey && active === last) {
            event.preventDefault();

            first.focus();

            return;
        }
    };

    /**
     * Handles touch start for mobile dragging.
     * @param {TouchEvent} event The touch start event.
     * @private
     *
     * @memberof DialogWindow
     */
    #handleTouchStart = (event) => {
        if (this.headless || this.#isResizing || event.target !== this.#dialogHeader) {
            return;
        }

        event.preventDefault();
        const touch = event.touches[0];

        this.#isDragging = true;
        this.#dialogHeader.classList.add('dragging');

        const dialogRect = this.#dialogContainer.getBoundingClientRect();

        this.#clickOffsetX = touch.clientX - dialogRect.left;
        this.#clickOffsetY = touch.clientY - dialogRect.top;

        // Set initial position.
        this.#prepareForPositioning();
        this.#dialogContainer.style.top = `${dialogRect.top}px`;
        this.#dialogContainer.style.left = `${dialogRect.left}px`;
    };
    // #endregion ********************************************************************************/

    // #region: Static methods *******************************************************************/
    /**
     * Configures a dialog element with the provided options.
     * @param {DialogWindow} dialogElement The dialog element to configure.
     * @param {object} options The configuration options.
     * @param {boolean} [skipData=false] Whether to skip data handling (for open method).
     * @static
     * @private
     *
     * @memberof DialogWindow
     */
    static #configureDialog(dialogElement, options, skipData = false) {
        const {
            autoSize = false,
            clampToViewport = false,
            close = null,
            data = null,
            headless = false,
            height = 741,
            icon,
            id,
            modal = true,
            title,
            width = 1025
        } = options;

        // Set attributes if provided.
        if (id) {
            dialogElement.id = id;
        }

        if (icon) {
            dialogElement.setAttribute('icon', icon);
        }

        if (title !== undefined) {
            dialogElement.setAttribute('data-dialog-title', title);
        }

        if (width !== undefined) {
            dialogElement.setAttribute('width', width.toString());
        }

        if (height !== undefined) {
            dialogElement.setAttribute('height', height.toString());
        }

        if (modal !== undefined) {
            dialogElement.setAttribute('modal', modal.toString());
        }

        if (autoSize !== undefined) {
            dialogElement.setAttribute('auto-size', autoSize.toString());
        }

        if (clampToViewport !== undefined) {
            dialogElement.setAttribute('clamp-to-viewport', clampToViewport.toString());
        }

        if (headless !== undefined && headless) {
            // Set headless as internal property, not attribute.
            dialogElement.isHeadless = true;
        }

        // Set data if provided and not skipped.
        if (data && !skipData) {
            // Element not yet initialized, store for later.
            dialogElement.pendingData = data;
        }

        // Set close callback if provided.
        if (close && typeof close === 'function') {
            dialogElement.setCloseCallback(close);
        }
    }

    /**
     * Creates a dialog-window programmatically and appends it to the specified owner window.
     * The dialog is created once and can be reused multiple times by calling the open() method.
     * When the dialog is closed, the reference remains valid for future use.
     * @example
     * // Create a dialog once and store the reference
     * const dialog = DialogWindow.create({
     *     id: 'my-dialog',
     *     title: 'My Dialog',
     *     owner: 'current',
     *     width: 800,
     *     height: 600,
     *     close: (event) => {
     *         console.log('Dialog closed:', event);
     *         // Custom cleanup logic here
     *     }
     * });
     *
     * // Open the dialog with content
     * dialog.open('/some/url', { title: 'Updated Title' });
     *
     * // Later, reuse the same dialog
     * dialog.open('/other/url', { title: 'Different Content' });
     *
     * // Clean up when completely done (optional)
     * dialog.destroy();
     * @param {object} [options={}] Configuration options for the dialog.
     * @param {boolean} [options.autoSize=false] Whether to automatically size the dialog to fit its content. When true, calculates content dimensions and resizes dialog before showing. Limited to 95% of viewport size.
     * @param {boolean} [options.clampToViewport=false] Whether to clamp the dialog to viewport boundaries.
     * @param {Function} [options.close] Callback function to execute when the dialog closes. Receives the close event as parameter.
     * @param {object} [options.data=null] Any data to associate with the dialog.
     * @param {boolean} [options.headless=false] Whether to create a headless dialog without header, borders, or resize handles. When true, always uses 'top' window regardless of owner setting.
     * @param {number} [options.height=741] The initial height of the dialog in pixels. Ignored when autoSize is true.
     * @param {string} [options.icon] The icon class name for the dialog header (e.g., 'lf-icon-pallet'). Ignored for headless dialogs.
     * @param {string} [options.id] The unique ID to assign to the dialog element for DOM queries.
     * @param {boolean} [options.modal=true] Whether to open as a modal dialog (blocks interaction with page).
     * @param {string} [options.owner] Where to create the dialog (ignored if headless=true):
     *   - 'current' (default): Creates in the current window/document.
     *   - 'parent': Creates in the parent window (useful for iframes).
     *   - 'top': Creates in the top-most window.
     * @param {string} [options.title] The title to display in the dialog header.
     * @param {number} [options.width=1025] The initial width of the dialog in pixels. Ignored when autoSize is true.
     * @returns {DialogWindow} The created dialog-window element reference.
     * @static
     *
     * @memberof DialogWindow
     */
    static create(options = {}) {
        const { owner, headless, ...dialogOptions } = options;

        // Headless dialogs always use 'top' window.
        let targetWindow;
        if (headless) {
            targetWindow = window.top;
        }
        else {
            // Determine target window based on owner parameter.
            switch (owner) {
                case 'parent':
                    targetWindow = window.parent;

                    break;

                case 'top':
                    targetWindow = window.top;

                    break;

                default:
                    targetWindow = window;

                    break;
            }
        }

        // Create the dialog-window element.
        const dialogElement = targetWindow.document.createElement('dialog-window');

        // Apply configuration options, including headless.
        DialogWindow.#configureDialog(dialogElement, { headless, ...dialogOptions });

        // Set the origin window reference for postMessage communication.
        // This allows iframe content to send messages back to the creating window.
        dialogElement.originWindow = window;
        
        // Set the target window reference on the dialog element.
        dialogElement.targetWindow = targetWindow;

        // Append to the target document's body.
        targetWindow.document.body.appendChild(dialogElement);

        return dialogElement;
    }   
    // #endregion ********************************************************************************/

    // #region: Public methods *******************************************************************/
    /**
     * Closes the dialog.
     *
     * @memberof DialogWindow
     */
    close() {
        if (this.headless) {
            // Native dialog close with animation.
            this.#dialog.classList.remove('show');
            this.#dialog.addEventListener('transitionend', () => {
                this.#dialog.close();
            }, { once: true });
        }
        else {
            // Close dialog immediately while backdrop may fade out.
            this.#dialog.classList.remove('show');

            // Close the dialog container immediately.
            this.#dialog.style.display = 'none';

            // Remove from stacking order and trigger close handlers immediately.
            const idx = DialogWindow.#openDialogs.indexOf(this);

            if (idx !== -1) {
                DialogWindow.#openDialogs.splice(idx, 1);
                DialogWindow.#recomputeZOrder();
            }

            this.#handleDialogClose();

            // Let backdrop fade out independently if it exists.
            if (this.#dialogBackdrop) {
                this.#dialogBackdrop.classList.remove('show');

                // Hide backdrop after transition completes.
                this.#dialogBackdrop.addEventListener('transitionend', () => {
                    this.#dialogBackdrop.style.display = 'none';
                }, { once: true });
            }
        }
    }

    /**
     * Destroys the dialog and removes it from the DOM.
     * This method properly cleans up all resources and should be used for
     * programmatically created dialogs when they are no longer needed to prevent memory leaks.
     * @example
     * // Create and use a dialog
     * const dialog = DialogWindow.create({ id: 'temp-dialog' });
     * dialog.open('/some/url');
     *
     * // Later, when completely done with the dialog
     * dialog.destroy();
     *
     * // Dialog is now removed from DOM and memory
     * console.log(document.getElementById('temp-dialog')); // null
     *
     * @memberof DialogWindow
     */
    destroy() {
        // Close the dialog first if it's open.
        if (this.isOpen) {
            this.#closeDialogInternal();
        }

        // Force cleanup of dialog close handler to prevent memory leaks.
        this.#handleDialogClose();

        // Call the existing disconnectedCallback to clean up all resources.
        this.disconnectedCallback();
    }

    /**
     * Initializes this web component.
     * Runs on document load.
     */
    initialize() {
        // Build the component's internal DOM structure.
        this.innerHTML = this.#getDialogHTML();

        // Get references to the newly created internal elements.
        if (this.headless) {
            this.#dialog = this.querySelector('dialog');
            this.#dialogBackdrop = null;
        }
        else {
            this.#dialogBackdrop = this.querySelector('.dialog-backdrop');
            this.#dialog = this.querySelector('.dialog-wrapper');
        }
        this.#dialogContainer = this.querySelector('.dialog-container');
        this.#dialogHeader = this.querySelector('.dialog-header');
        this.#dialogTitleElement = this.querySelector('.dialog-title');
        this.#iframe = this.querySelector('iframe');
        this.#closeButton = this.querySelector('.dialog-close-button, .dialog-floating-close-button'); // regular or floating button
        this.#resizeHandles = this.querySelectorAll('.resize-handle'); // empty in headless mode

        // Set up ARIA labelledby relationship.
        if (this.#dialogContainer && this.#dialogTitleElement) {
            this.#dialogContainer.setAttribute('aria-labelledby', 'dialog-title');
        }

        // Attach all necessary event listeners using AbortController for cleanup.
        const { signal } = this.#abortController;

        // Set dialogWindow reference.
        if (this.#iframe) {
            this.#iframe.dialogWindow = this;
        }
        else {
            window.dialogWindow = this;
        }

        // Store dialog data as a data attribute for early access.
        this.#iframe.setAttribute('data-dialog-data', JSON.stringify(this.#data || {}));

        // Close button only exists in non-headless mode.
        if (this.#closeButton) {
            this.#closeButton.addEventListener('click', this.#handleCloseButtonClick, { signal });
        }

        // Listen for close event only on native dialog (headless).
        if (this.headless && this.#dialog) {
            this.#dialog.addEventListener('close', this.#handleDialogClose, { signal });
        }
        
        // Header dragging only for non-headless mode.
        if (this.#dialogHeader) {
            this.#dialogHeader.addEventListener('mousedown', this.#handleHeaderDown, { signal });
        }
        
        // Resize handles only exist in non-headless mode.
        this.#resizeHandles.forEach((handle) => {
            handle.addEventListener('mousedown', this.#handleResizeHandleDown, { signal });
        });

        // Global listeners for resize/drag operations.
        document.addEventListener('mousemove', this.#handleResizeOrDrag, { signal });
        document.addEventListener('mouseup', this.#handleStopResizeOrDrag, { signal });

        // Touch support - only for non-headless mode.
        if (this.#dialogHeader) {
            this.#dialogHeader.addEventListener('touchstart', this.#handleTouchStart, { passive: false, signal });
            document.addEventListener('touchmove', this.#handleTouchMove, { passive: false, signal });
            document.addEventListener('touchend', this.#handleTouchEnd, { signal });
        }

        // Keyboard support.
        this.addEventListener('keydown', this.#handleKeyDown, { signal });
        this.addEventListener('keydown', this.#trapFocus, { signal });

        // Document-level ESC key support for headless dialogs.
        if (this.headless) {
            document.addEventListener('keydown', this.#handleDocumentKeyDown, { signal });
        }

        // Set up resize observer for responsive adjustments.
        this.#setupResizeObserver();

        // Apply initial attributes if any.
        this.#applyInitialAttributes();

        // Handle pending data from static factory method.
        if (this.pendingData) {
            this.#data = this.pendingData;
            delete this.pendingData;
        }

        // Apply headless mode if set via create method.
        if (this.isHeadless) {
            this.#updateHeadlessMode();
        }
    }

    /**
     * Opens the dialog.
     * @param {string|HTMLElement} [urlOrContent] The URL for iframe content (string) or DOM content (HTMLElement).
     * @param {object} [options={}] Configuration options for the dialog.
     * @param {boolean} [options.autoSize=false] Whether to automatically size the dialog to fit its content.
     * @param {boolean} [options.clampToViewport=false] Whether to clamp the dialog to viewport boundaries.
     * @param {object} [options.data=null] Any data passed to this dialog.
     * @param {number} [options.height=741] The initial height of the dialog. Ignored when autoSize is true.
     * @param {boolean} [options.modal=true] Whether to open as a modal.
     * @param {string} [options.title] The title to display in the dialog header.
     * @param {number} [options.width=1025] The initial width of the dialog. Ignored when autoSize is true.
     *
     * @memberof DialogWindow
     */
    open(urlOrContent, options = {}) {
        // Determine if first parameter is URL (string) or content (DOM node).
        const isUrl = typeof urlOrContent === 'string';
        const isContent = urlOrContent instanceof HTMLElement;

        // Update attributes if URL is provided.
        if (isUrl) {
            this.setAttribute('src', urlOrContent);
        }
        
        // Make the origin window reference available to iframe content.
        // Store it on the dialog element so iframe can access via parent.
        this.dataset.originWindow = 'available';

        // Apply configuration options using shared method.
        DialogWindow.#configureDialog(this, options, true);

        // Set data directly since element is already initialized.
        if (options.data) {
            this.#data = options.data;
            // Update data attribute for iframe access.
            this.#iframe.setAttribute('data-dialog-data', JSON.stringify(this.#data));
        }

        // Handle content replacement if DOM content is provided.
        if (isContent) {
            const dialogContent = this.querySelector('.dialog-content');
            if (dialogContent) {
                // Clear existing content and add the new content.
                dialogContent.innerHTML = '';
                dialogContent.appendChild(urlOrContent);
            }
        }

        // Calculate auto-size dimensions if auto-sizing is enabled.
        let autoSizeDimensions = null;

        if (this.#autoSize && !this.headless) {
            autoSizeDimensions = this.#calculateAutoSize();

            if (autoSizeDimensions) {
                // Apply calculated dimensions before showing.
                this.setAttribute('width', autoSizeDimensions.width.toString());
                this.setAttribute('height', autoSizeDimensions.height.toString());
            }
        }

        // Open dialog.
        const modalValue = options.modal !== undefined ? options.modal : true;

        if (this.headless) {
            // Use native dialog methods for headless.
            if (modalValue) {
                this.#dialog.showModal();
            }
            else {
                this.#dialog.show();
            }

            // Add show class for animation
            requestAnimationFrame(() => {
                this.#dialog.classList.add('show');
            });
        }
        else {
            // Show dialog and backdrop for regular dialogs.
            this.#dialog.style.display = 'flex';

            if (this.#dialogBackdrop) {
                if (modalValue) {
                    this.#dialogBackdrop.style.display = 'block';
                }
                else {
                    this.#dialogBackdrop.style.display = 'none';
                }
            }

            // Maintain stacking order for non-headless dialogs.
            if (!DialogWindow.#openDialogs.includes(this)) {
                DialogWindow.#openDialogs.push(this);
            }

            DialogWindow.#recomputeZOrder();

            // Open with animation (when not disabled).
            requestAnimationFrame(() => {
                this.#dialog.classList.add('show');

                if (this.#dialogBackdrop && modalValue) {
                    this.#dialogBackdrop.classList.add('show');
                }
            });
        }

        // Prevent background scroll.
        this.#lockScroll();

        // Center dialog (skip for headless).
        if (!this.headless) {
            this.#centerDialog();
        }

        // Set up history management for back button behavior.
        this.#setupHistoryManagement();

        // Dispatch open event.
        this.dispatchEvent(new CustomEvent('dialogopen', {
            bubbles: true,
            composed: true
        }));
    }

    /**
     * Sends a message to the origin window from this dialog's iframe content.
     * This method should be called from iframe content to communicate back to the origin.
     * 
     * @example
     * // From within an iframe in the dialog
     * DialogWindow.instance.sendMessage({ action: 'saveInspection', data: formData });
     * @param {object} message The message object to send.
     *
     * @memberof DialogWindow
     */
    sendMessage(message) {
        const sanitizedMessage = coreUI.sanitizeForPostMessage(message);

        if (this.#originWindow) {
            const targetOrigin = this.#originWindow.location?.origin ?? window.location.origin;

            this.#originWindow.postMessage(sanitizedMessage, targetOrigin);
        }
        else {
            // Fallback for backward compatibility.
            const targetOrigin = window.parent.location?.origin ?? window.location.origin;

            window.parent.postMessage(sanitizedMessage, targetOrigin);
        }
    }

    /**
     * Sets the close callback function.
     * This function will be called when the dialog is closed.
     * @param {Function} callback The callback function to execute on close.
     *
     * @memberof DialogWindow
     */
    setCloseCallback(callback) {
        this.#closeCallback = callback;
    }
    // #endregion ********************************************************************************/

    // #region: Private methods ******************************************************************/
    /**
     * Adjusts the dialog dimensions to fit within the current viewport.
     * Re-runs auto-sizing when enabled; otherwise clamps the requested width and height to viewport-safe maxima.
     * @private
     *
     * @memberof DialogWindow
     */
    #adjustToViewport() {
        if (this.headless || !this.#dialogContainer) {
            return;
        }

        // Re-run auto-size calculation when enabled — it adapts to the new viewport.
        if (this.#autoSize) {
            const dimensions = this.#calculateAutoSize();

            if (dimensions) {
                this.setAttribute('width', dimensions.width.toString());
                this.setAttribute('height', dimensions.height.toString());
            }

            return;
        }

        // Compute viewport-safe maxima with the same margins as auto-size.
        const viewport = window.visualViewport ?? { height: window.innerHeight, width: window.innerWidth };
        const isMobile = viewport.width < DialogWindow.SMALL_SCREEN_BREAKPOINT;
        const marginInline = isMobile ? 32 : 64;
        const marginBlock = isMobile ? 64 : 128;
        const maxWidth = Math.max(DialogWindow.MIN_DIALOG_WIDTH, viewport.width - marginInline);
        const maxHeight = Math.max(DialogWindow.MIN_DIALOG_HEIGHT, viewport.height - marginBlock);

        // The attributes carry the requested size (set on open() and on user resize end).
        const requestedWidth = parseInt(this.getAttribute('width'), 10) || maxWidth;
        const requestedHeight = parseInt(this.getAttribute('height'), 10) || maxHeight;

        this.#dialogContainer.style.width = `${Math.min(requestedWidth, maxWidth)}px`;
        this.#dialogContainer.style.height = `${Math.min(requestedHeight, maxHeight)}px`;
    }

    /**
     * Applies initial attributes if present.
     * @private
     *
     * @memberof DialogWindow
     */
    #applyInitialAttributes() {
        const attributes = ['auto-size', 'clamp-to-viewport', 'data-dialog-title', 'height', 'modal', 'src', 'width'];

        attributes.forEach((attr) => {
            if (this.hasAttribute(attr)) {
                this.attributeChangedCallback(attr, null, this.getAttribute(attr));
            }
        });
    }

    /**
     * Calculates the optimal dimensions for auto-sizing the dialog based on content.
     * Uses modern viewport units and visualViewport API for better mobile browser support.
     * Handles flexbox content that may wrap when constrained to specific widths.
     * @returns {object} Object containing width and height properties.
     * @private
     *
     * @memberof DialogWindow
     */
    #calculateAutoSize() {
        // Skip if auto-size is disabled or headless mode.
        if (!this.#autoSize || this.headless) {
            return null;
        }

        // Use visualViewport for better mobile support, fallback to window dimensions.
        const getViewport = () => {
            return window.visualViewport ?? {
                width: window.innerWidth,
                height: window.innerHeight
            };
        };

        const { width: viewportWidth, height: viewportHeight } = getViewport();
        
        // Calculate available space using logical viewport units.
        const isMobile = viewportWidth < DialogWindow.SMALL_SCREEN_BREAKPOINT;
        const marginInline = isMobile ? 32 : 64;
        const marginBlock = isMobile ? 64 : 128;
        
        // Use modern viewport calculations.
        const maxWidth = Math.max(0, viewportWidth - marginInline);
        const maxHeight = Math.max(0, viewportHeight - marginBlock);

        // Temporarily show dialog off-screen for measurement.
        const originalDisplay = this.#dialog.style.display;
        const originalVisibility = this.#dialog.style.visibility;
        const originalPosition = this.#dialog.style.position;

        this.#dialog.style.display = 'flex';
        this.#dialog.style.visibility = 'hidden';
        this.#dialog.style.position = 'fixed';
        this.#dialog.style.top = '-9999px';
        this.#dialog.style.left = '-9999px';

        // Add measurement class for clean content measurement.
        this.#dialogContainer.classList.add('auto-size-measuring');

        // First pass: measure natural width without constraints.
        this.#dialogContainer.style.width = 'auto';
        this.#dialogContainer.style.height = 'auto';
        this.#dialogContainer.style.maxWidth = 'none';
        this.#dialogContainer.style.maxHeight = 'none';

        // Force layout recalculation.
        this.#dialogContainer.offsetHeight;

        // Get natural dimensions.
        const naturalRect = this.#dialogContainer.getBoundingClientRect();
        let naturalWidth = Math.ceil(naturalRect.width);
        let naturalHeight = Math.ceil(naturalRect.height);

        // Apply minimum constraints.
        naturalWidth = Math.max(naturalWidth, DialogWindow.MIN_DIALOG_WIDTH);
        naturalHeight = Math.max(naturalHeight, DialogWindow.MIN_DIALOG_HEIGHT);

        // Determine final width considering viewport constraints.
        const finalWidth = Math.min(naturalWidth, maxWidth);

        // Second pass: measure height with constrained width to handle flexbox wrapping.
        if (finalWidth < naturalWidth) {
            this.#dialogContainer.style.width = `${finalWidth}px`;
            this.#dialogContainer.style.height = 'auto';
            this.#dialogContainer.style.maxWidth = `${finalWidth}px`;
            this.#dialogContainer.style.maxHeight = `${maxHeight}px`;

            // Force layout recalculation with constrained width.
            this.#dialogContainer.offsetHeight;

            // Measure height with wrapped content.
            const constrainedRect = this.#dialogContainer.getBoundingClientRect();
            naturalHeight = Math.ceil(constrainedRect.height);

            // Reapply minimum height constraint.
            naturalHeight = Math.max(naturalHeight, DialogWindow.MIN_DIALOG_HEIGHT);
        }

        // Apply maximum height constraint.
        const finalHeight = Math.min(naturalHeight, maxHeight);

        // Remove measurement class and restore original styles.
        this.#dialogContainer.classList.remove('auto-size-measuring');
        this.#dialog.style.display = originalDisplay;
        this.#dialog.style.visibility = originalVisibility;
        this.#dialog.style.position = originalPosition;
        this.#dialog.style.removeProperty('top');
        this.#dialog.style.removeProperty('left');

        return {
            width: finalWidth,
            height: finalHeight
        };
    }

    /**
     * Centers the dialog in the viewport.
     * @private
     *
     * @memberof DialogWindow
     */
    #centerDialog() {
        // Skip centering for headless dialogs.
        if (this.headless) {
            return;
        }
        
        this.#dialogContainer.style.insetBlockStart = '50%';
        this.#dialogContainer.style.insetInlineStart = '50%';
        this.#dialogContainer.style.transform = 'translate(-50%, -50%)';
        this.#dialogContainer.style.removeProperty('top');
        this.#dialogContainer.style.removeProperty('left');
    }

    /**
     * Cleans up dialog state: clears iframe content and removes the popstate listener.
     * @private
     *
     * @memberof DialogWindow
     */
    #cleanupHistoryManagement() {
        // Clear iframe content to prevent it from lingering in memory.
        // Use location.replace() to avoid adding an entry to the joint session history.
        this.#iframe?.contentWindow?.location.replace('about:blank');

        if (this.#historyStateKey) {
            window.removeEventListener('popstate', this.#popstateHandler);
            this.#historyStateKey = null;
            this.#popstateHandler = null;
        }
    }

    /**
     * Closes the dialog without animation.
     * @private
     *
     * @memberof DialogWindow
     */
    #closeDialogInternal() {
        if (this.headless) {
            this.#dialog.close();
        }
        else {
            this.#dialog.style.display = 'none';
            if (this.#dialogBackdrop) {
                this.#dialogBackdrop.style.display = 'none';
            }

            // Remove from stacking order and recompute z-index layering.
            const idx = DialogWindow.#openDialogs.indexOf(this);

            if (idx !== -1) {
                DialogWindow.#openDialogs.splice(idx, 1);
                DialogWindow.#recomputeZOrder();
            }
        }

        this.#handleDialogClose();
    }

    /**
     * Gets the HTML string for the dialog's internal structure.
     * @returns {string} The HTML string.
     * @private
     *
     * @memberof DialogWindow
     */
    #getDialogHTML() {
        const closeDialogText = this.#locales?.closeDialog || 'Dialoog sluiten';
        const dialogContentText = this.#locales?.dialogContent || 'Dialoog Inhoud';
        
        let dialog = '';
        
        // Use native dialog for headless, div-based for regular.
        if (this.headless) {
            dialog = '<dialog role="dialog" aria-modal="true"><div class="dialog-container">';
        }
        else {
            dialog = '<div class="dialog-backdrop"></div>';
            dialog += '<div class="dialog-wrapper"><div class="dialog-container" role="dialog" aria-modal="true">';
        }

        // Add resize handles only if not headless
        if (!this.headless) {
            dialog += `
                       <div class="resize-handle top-left"></div>
                       <div class="resize-handle top"></div>
                       <div class="resize-handle top-right"></div>
                       <div class="resize-handle left"></div>
                       <div class="resize-handle right"></div>
                       <div class="resize-handle bottom-left"></div>
                       <div class="resize-handle bottom"></div>
                       <div class="resize-handle bottom-right"></div>`;
        }

        // Add header only if not headless
        if (!this.headless) {
            dialog += '<div class="dialog-header">';

            if (this.icon) {
                dialog += `<span class="dialog-window-icon ${this.icon}" aria-hidden="true"></span>`;
            }

            dialog += `<h2 class="dialog-title" id="dialog-title"></h2>
                           <button type="button" class="dialog-close-button" aria-label="${closeDialogText}">
                               &times;
                           </button>
                       </div>`;
        }
        else {
            // Add floating close button for headless mode
            dialog += `<button type="button" class="dialog-floating-close-button" aria-label="${closeDialogText}">
                           &times;
                       </button>`;
        }

        dialog += `<div class="dialog-content">
                           <iframe src="about:blank" title="${dialogContentText}"></iframe>
                       </div>
                   </div>`;
        
        // Close the appropriate wrapper tag.
        if (this.headless) {
            dialog += '</dialog>';
        }
        else {
            dialog += '</div>';
        }

        return dialog;
    }

    /**
     * Locks scroll to prevent background scrolling when dialog is open.
     * @private
     *
     * @memberof DialogWindow
     */
    #lockScroll() {
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        if (scrollbarWidth > 0) {
            document.body.style.setProperty('--scrollbar-width', `${scrollbarWidth}px`);
        }

        document.documentElement.style.overflow = 'hidden';
        document.body.classList.add('no-scroll');
    }

    /**
     * Prepares the dialog for manual positioning.
     * @private
     *
     * @memberof DialogWindow
     */
    #prepareForPositioning() {
        this.#dialogContainer.style.insetBlockStart = 'auto';
        this.#dialogContainer.style.insetInlineStart = 'auto';
        this.#dialogContainer.style.transform = 'none';
        this.#dialogContainer.style.position = 'fixed';
    }

    /**
     * Sets up history state management to handle back button behavior.
     * Pushes a history state when dialog opens and listens for popstate events.
     * @private
     *
     * @memberof DialogWindow
     */
    #setupHistoryManagement() {
        // Generate unique key for this dialog's history state.
        this.#historyStateKey = `dialog-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

        // Push a new history state so back button has an entry to consume.
        const currentState = history.state || {};
        history.pushState({ ...currentState, [this.#historyStateKey]: true }, '');

        // Close the dialog when the user navigates back past this entry.
        this.#popstateHandler = (event) => {
            if (this.isOpen && !event.state?.[this.#historyStateKey]) {
                this.close();
            }
        };

        window.addEventListener('popstate', this.#popstateHandler);
    }

    /**
     * Sets up resize observer for responsive adjustments with debouncing.
     * @private
     *
     * @memberof DialogWindow
     */
    #setupResizeObserver() {
        let rafId = 0;

        const debouncedResize = () => {
            cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(() => {
                if (this.isOpen && !this.#isResizing && !this.#isDragging && !this.headless) {
                    this.#adjustToViewport();
                    this.#centerDialog();
                }
            });
        };

        this.#resizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
                if (entry.target === document.body) {
                    debouncedResize();
                }
            }
        });

        this.#resizeObserver.observe(document.body);

        // Also listen for viewport changes on mobile.
        if (window.visualViewport) {
            window.visualViewport.addEventListener('resize', debouncedResize, {
                passive: true,
                signal: this.#abortController.signal
            });
        }

        // Fallback listeners for browsers where visualViewport is unreliable during orientation changes.
        window.addEventListener('resize', debouncedResize, {
            passive: true,
            signal: this.#abortController.signal
        });

        window.addEventListener('orientationchange', debouncedResize, {
            passive: true,
            signal: this.#abortController.signal
        });
    }

    /**
     * Stops the dragging process.
     * @returns {void}
     * @private
     *
     * @memberof DialogWindow
     */
    #stopDrag() {
        if (!this.#isDragging) {
            return;
        }

        this.#isDragging = false;
        
        if (this.#dialogHeader) {
            this.#dialogHeader.classList.remove('dragging');
        }

        document.body.style.userSelect = '';
        document.body.style.cursor = '';
    }

    /**
     * Stops the resizing process.
     * @returns {void}
     * @private
     *
     * @memberof DialogWindow
     */
    #stopResize() {
        if (!this.#isResizing) {
            return;
        }

        this.#isResizing = false;
        document.body.style.userSelect = '';
        document.body.style.cursor = '';

        this.#dialogContainer.classList.remove('resizing');
        this.#currentHandle = null;

        // Persist the user's chosen dimensions so viewport adjustments preserve intent.
        this.setAttribute('width', this.#dialogContainer.offsetWidth.toString());
        this.setAttribute('height', this.#dialogContainer.offsetHeight.toString());
    }

    /**
     * Unlocks scroll to restore background scrolling when dialog is closed.
     * @private
     *
     * @memberof DialogWindow
     */
    #unlockScroll() {
        document.documentElement.style.overflow = '';
        document.body.classList.remove('no-scroll');
        document.body.style.removeProperty('--scrollbar-width');
    }

    /**
     * Updates the dialog mode based on headless attribute.
     * @private
     *
     * @memberof DialogWindow
     */
    #updateHeadlessMode() {
        const isHeadless = this.headless;

        if (isHeadless) {
            this.#dialogContainer.classList.add('headless');
        }
        else {
            this.#dialogContainer.classList.remove('headless');
        }
    }
    // #endregion ********************************************************************************/

}
// #endregion ************************************************************************************/

/**
 * Register web component.
 * Defines the custom element 'dialog-window' and maps it to the DialogWindow class.
 */
customElements.define(DialogWindow.tagName, DialogWindow);
